"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var d3 = _interopRequireWildcard(require("d3"));
var _useChartDimensions = require("../hooks/useChartDimensions");
var _useChartTooltip = require("../hooks/useChartTooltip");
var _SvgChart = _interopRequireDefault(require("../components/SvgChart"));
var _labelFormatter = require("../utils/label-formatter");
var _useChartData = require("../hooks/useChartData");
var _LineChart = require("./LineChart.styled");
var _useAxisOverlap = require("../hooks/useAxisOverlap");
var _constants = require("../constants");
var _chartOperations = require("../utils/chart-operations");
var _useFilter = require("../hooks/useFilter");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const ANIMATION_DURATION = 800;
const Y_AXIS_PADDING = 0.1;
const BASE_MARGIN = Object.freeze({
  top: 10,
  bottom: 40,
  left: 30,
  right: 0
});
const POINT_RADIUS = 6;
const LineChart = /*#__PURE__*/(0, _react.memo)(chartProps => {
  var _chart$colors, _xLabels$at, _xLabels$at2, _data$0$points$at, _data$0$points$at2;
  const {
    chart,
    animate,
    chartColors
  } = chartProps;
  const {
    xCol,
    yCol,
    yFormat
  } = chart;
  const {
    showPoints = true,
    xAxisFormat = "auto",
    zeroBased = false
  } = chart.componentProps;
  const palette = ((_chart$colors = chart.colors) === null || _chart$colors === void 0 ? void 0 : _chart$colors.schemes.categorical) || chartColors.schemes.categorical;
  const {
    filter,
    filterComponent
  } = (0, _useFilter.useFilter)(chartProps);
  const [animationDone, setAnimationDone] = (0, _react.useState)(!animate);
  const svgRef = _react.default.useRef(null);
  const {
    detectLabelOverlap,
    labelOverlap
  } = (0, _useAxisOverlap.useAxisOverlap)(svgRef, ".x-axis");
  const {
    width,
    height,
    margin
  } = (0, _useChartDimensions.useChartDimensions)(svgRef, BASE_MARGIN, chart.spanRow, ".x-axis text, .y-axis text, text.line-label");
  const {
    data,
    xLabels,
    yMin,
    yMax
  } = (0, _useChartData.useChartData)(chart, filter, true);
  const {
    tooltipRef,
    showTooltip,
    hideTooltip
  } = (0, _useChartTooltip.useChartTooltip)(chart, data);
  data.forEach(d => d.points.sort((p1, p2) => new Date(p1[xCol]).getTime() - new Date(p2[xCol]).getTime()));
  xLabels.sort((l1, l2) => new Date(l1.key).getTime() - new Date(l2.key).getTime());

  // TODO: Keep x-axis fixed if fixedScale is true
  const xScale = d3.scaleTime().domain([(0, _chartOperations.stringToTimestamp)((_xLabels$at = xLabels.at(0)) === null || _xLabels$at === void 0 ? void 0 : _xLabels$at.key), (0, _chartOperations.stringToTimestamp)((_xLabels$at2 = xLabels.at(-1)) === null || _xLabels$at2 === void 0 ? void 0 : _xLabels$at2.key)]).range([0, width]);
  const yRange = Math.abs(yMax - yMin);
  const yPadding = yRange * Y_AXIS_PADDING;
  const yScale = d3.scaleLinear().domain([yMin < 0 ? yMin - yPadding : !zeroBased ? Math.max(yMin - yPadding, 0) : 0, yMax + yPadding]).range([height, 0]);
  const yLine = d3.line().x(d => xScale((0, _chartOperations.stringToTimestamp)(d[xCol]))).y(d => yScale(Number(d[yCol])));
  const colorPalette = d3.scaleOrdinal().domain(data.map(d => d.seriesKey)).range(palette);
  (0, _react.useLayoutEffect)(() => {
    if (svgRef.current === null || height == null) {
      return;
    }
    const mainGroup = d3.select(svgRef.current).select(".main-group");
    const isAutoFormat = xAxisFormat === "auto";
    let prevYear = null;
    const xAxis = d3.axisBottom(xScale).tickSize(labelOverlap ? 5 : 0).tickPadding(labelOverlap ? 2 : 8).tickFormat(xCol === _constants.COL_YEAR ? d3.timeFormat(isAutoFormat ? "%Y" : xAxisFormat) : (date, idx) => {
      let dateString = "";
      if (idx === 0 || prevYear !== d3.timeFormat(isAutoFormat ? "%Y" : xAxisFormat)(date)) {
        dateString = d3.timeFormat(isAutoFormat ? "%b/%y" : xAxisFormat)(date);
      } else {
        dateString = d3.timeFormat(isAutoFormat ? "%b" : xAxisFormat)(date);
      }
      prevYear = d3.timeFormat(isAutoFormat ? "%Y" : xAxisFormat)(date);
      return dateString;
    }).ticks(xCol === _constants.COL_YEAR ? xLabels.length : undefined);
    mainGroup.select(".x-axis").classed("x-axis", true).attr("transform", "translate(0, ".concat(height, ")")).call(xAxis).selectAll(".tick text").style("text-anchor", labelOverlap ? "end" : "middle");
    let yAxis = d3.axisLeft(yScale).tickSize(0).tickPadding(8).ticks(8).tickFormat(v => _labelFormatter.QuantitativeFormatter[yFormat](v.toString()));
    mainGroup.select(".y-axis").classed("y-axis", true).call(yAxis);
    detectLabelOverlap();
  }, [xScale, yScale, xLabels, height, labelOverlap]);
  (0, _react.useLayoutEffect)(() => {
    if (!svgRef.current) {
      return;
    }
    const labels = svgRef.current.querySelectorAll(".line-chart text.line-label");
    for (let i = 0; i < labels.length; i++) {
      const label1 = labels[i].getBBox();
      for (let j = i + 1; j < labels.length; j++) {
        const label2 = labels[j].getBBox();
        if ((0, _chartOperations.boxesIntersect)(label1, label2)) {
          const moveFirst = label1.y < label2.y;
          const moveIdx = moveFirst ? i : j;
          const diff = 14 - Math.floor(Math.abs(label1.y - label2.y));
          const dy = Number(labels[j].getAttribute("dy")) || 0;
          labels[moveIdx].setAttribute("dy", (dy - diff).toString());
          break;
        }
      }
    }
    return () => {
      for (const label of labels) {
        label.setAttribute("dy", "3");
      }
    };
  }, [svgRef.current, chart]);
  (0, _react.useLayoutEffect)(() => {
    if (!svgRef.current) {
      return;
    }
    const circles = Array.from(svgRef.current.querySelectorAll(".line-chart circle"));
    const xSorted = [...circles.sort((a, b) => Number(a.getAttribute("cx")) - Number(b.getAttribute("cx")))];
    const ySorted = [...circles.sort((a, b) => Number(a.getAttribute("cy")) - Number(b.getAttribute("cy")))];
    const coordinates = {
      x: xSorted.map(circle => Number(circle.getAttribute("cx"))),
      y: ySorted.map(circle => Number(circle.getAttribute("cy")))
    };
    const OFFSET = 30;
    d3.select(svgRef.current).on("mousemove", event => {
      const selected = [];
      const [px, py] = d3.pointer(event);
      const x = px - margin.left;
      const y = py - margin.top;
      if (x >= 0) {
        const xLowerIndex = coordinates.x.findIndex(cx => cx >= x - OFFSET);
        const xUpperIndex = coordinates.x.findIndex(cx => cx >= x + OFFSET);
        if (xLowerIndex !== -1 && xUpperIndex !== -1) {
          selected.push(...xSorted.slice(xLowerIndex, xUpperIndex + 1));
        }
      }
      if (y >= 0) {
        const yLowerIndex = coordinates.y.findIndex(cy => cy >= y - OFFSET);
        const yUpperIndex = coordinates.y.findIndex(cy => cy >= y + OFFSET);
        if (yLowerIndex !== -1 && yUpperIndex !== -1) {
          selected.push(...ySorted.slice(yLowerIndex, yUpperIndex + 1));
        }
      }
      const maxDistance = Math.max(300 / data.length, 15);
      const closestCircle = findClosestCircle({
        x,
        y
      }, selected, maxDistance);
      if (closestCircle) {
        const yCircle = Number(closestCircle.getAttribute("cy"));
        showTooltip(closestCircle, yCircle > y ? "bottom" : "top");
        if (!showPoints) {
          svgRef.current.querySelectorAll(".line-chart circle.visible").forEach(circle => circle.classList.remove("visible"));
        }
        closestCircle.classList.add("visible");
        const seriesKey = closestCircle.dataset.series;
        svgRef.current.querySelectorAll(".line-chart path").forEach(path => path.classList.add("semitransparent"));
        svgRef.current.querySelector(".line-chart path[data-series=\"".concat(seriesKey, "\"]")).classList.remove("semitransparent");
      } else {
        if (!showPoints) {
          svgRef.current.querySelectorAll(".line-chart circle.visible").forEach(circle => circle.classList.remove("visible"));
        }
        svgRef.current.querySelectorAll(".line-chart path").forEach(path => path.classList.remove("semitransparent"));
        hideTooltip();
      }
    });
    d3.select(svgRef.current).on("mouseout", event => {
      if (!showPoints) {
        svgRef.current.querySelectorAll(".line-chart circle.visible").forEach(circle => circle.classList.remove("visible"));
      }
      svgRef.current.querySelectorAll(".line-chart path").forEach(path => path.classList.remove("semitransparent"));
      hideTooltip();
    });
    return () => {
      d3.select(svgRef.current).on("mousemove", null);
      d3.select(svgRef.current).on("mouseout", null);
    };
  }, [svgRef.current, margin, showPoints, data]);
  const handleVisible = (0, _react.useCallback)(() => {
    if (animationDone) {
      return;
    }
    svgRef.current.querySelectorAll(".line-chart path").forEach(path => {
      const length = path.getTotalLength();
      path.style.setProperty("--path-length", length.toString());
      path.classList.add("animated");
    });
    svgRef.current.querySelectorAll("circle").forEach(circle => {
      circle.classList.add("full");
    });
    window.setTimeout(() => setAnimationDone(true), ANIMATION_DURATION);
  }, [animationDone]);
  return /*#__PURE__*/_react.default.createElement(_SvgChart.default, {
    margin: margin,
    svgRef: svgRef,
    chart: chart,
    tooltipRef: tooltipRef,
    palette: palette,
    onVisible: handleVisible,
    filterComponent: filterComponent
  }, /*#__PURE__*/_react.default.createElement(_LineChart.LineChartGroup, {
    className: "line-chart"
  }, yMin < 0 && /*#__PURE__*/_react.default.createElement("line", {
    stroke: "lightgray",
    x1: xScale((0, _chartOperations.stringToTimestamp)((_data$0$points$at = data[0].points.at(0)) === null || _data$0$points$at === void 0 ? void 0 : _data$0$points$at[xCol])) - 20,
    y1: yScale(0),
    x2: xScale((0, _chartOperations.stringToTimestamp)((_data$0$points$at2 = data[0].points.at(-1)) === null || _data$0$points$at2 === void 0 ? void 0 : _data$0$points$at2[xCol])) + 20,
    y2: yScale(0)
  }), data.map(d => {
    var _d$points$at, _d$points$at2, _d$points$at3;
    return /*#__PURE__*/_react.default.createElement("g", {
      key: d.seriesKey
    }, /*#__PURE__*/_react.default.createElement("path", {
      className: "line ".concat(!animationDone ? "" : "full"),
      d: yLine(d.points),
      stroke: colorPalette(d.seriesKey),
      fill: "none",
      strokeWidth: 2,
      "data-series": d.seriesKey
    }), d.points.map(p => /*#__PURE__*/_react.default.createElement("circle", {
      className: !animationDone ? "".concat(showPoints ? "visible" : "") : "".concat(showPoints ? "visible" : "", " full"),
      key: "".concat(d.seriesKey, "-").concat(p[xCol]),
      cx: xScale((0, _chartOperations.stringToTimestamp)(p[xCol])),
      cy: yScale(Number(p[yCol])),
      r: POINT_RADIUS,
      fill: colorPalette(d.seriesKey),
      "data-x": p[xCol],
      "data-series": d.seriesKey,
      "data-color": colorPalette(d.seriesKey)
    })), /*#__PURE__*/_react.default.createElement("text", {
      className: "line-label",
      stroke: colorPalette(d.seriesKey),
      textAnchor: "start",
      dx: 8,
      dy: 3,
      x: xScale((0, _chartOperations.stringToTimestamp)((_d$points$at = d.points.at(-1)) === null || _d$points$at === void 0 ? void 0 : _d$points$at[xCol])),
      y: yScale(Number((_d$points$at2 = d.points.at(-1)) === null || _d$points$at2 === void 0 ? void 0 : _d$points$at2[yCol]))
    }, _labelFormatter.QuantitativeFormatter[yFormat]((_d$points$at3 = d.points.at(-1)) === null || _d$points$at3 === void 0 ? void 0 : _d$points$at3[yCol])));
  })), /*#__PURE__*/_react.default.createElement("g", {
    className: "x-axis"
  }), /*#__PURE__*/_react.default.createElement("g", {
    className: "y-axis"
  }));
});
var _default = LineChart;
exports.default = _default;
function findClosestCircle(point, circles) {
  let maxDistance = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 40;
  // Initialize variables to keep track of the closest circle and its distance to the point
  let closestCircle = null;
  let minDistance = Infinity;
  // Loop through each circle in the array
  circles.forEach(circle => {
    const cx = Number(circle.getAttribute('cx'));
    const cy = Number(circle.getAttribute('cy'));
    // Calculate the distance from the point to the circle's center
    const distance = Math.sqrt((point.x - cx) ** 2 + (point.y - cy) ** 2);
    // Update the closest circle and minimum distance if this circle is closer
    if (distance < minDistance) {
      closestCircle = circle;
      minDistance = distance;
    }
  });
  return minDistance <= maxDistance ? closestCircle : null;
}